import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, useEffect } from 'react';
import {
  UserUtilizationDto,
  UserMonthlyExperiencesDto,
} from '@repo/shared/dtos/analytics/UtilizationDto';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { BASE_URLS, CACHE_TIMES, DEFAULT_QUERY_OPTIONS, DEFAULTS } from './config';
import {
  fetchAnalyticsData,
  createAnalyticsUrl,
  normalizeAnalyticsFilters,
  createDefaultBaseFilters,
} from './utils';
import { analyticsQueryKeys } from './queryKeys';

interface UserSearchResult {
  first_name: string;
  last_name: string;
}

interface UseUserSearchOptions {
  searchTerm: string;
  filters?: BaseAnalyticsFilter;
  limit?: number;
  enabled?: boolean;
}

interface UseUserUtilizationOptions {
  firstName: string;
  lastName: string;
  filters?: BaseAnalyticsFilter;
  enabled?: boolean;
}

interface UseUserMonthlyExperiencesOptions {
  firstName: string;
  lastName: string;
  monthYear: string;
  filters?: BaseAnalyticsFilter;
  enabled?: boolean;
}

export const useUserSearch = ({
  searchTerm,
  filters,
  limit = DEFAULTS.USER_SEARCH_LIMIT,
  enabled = true,
}: UseUserSearchOptions) => {
  const defaultFilters = useMemo(() => createDefaultBaseFilters(), []);

  const query = useQuery<UserSearchResult[]>({
    queryKey: analyticsQueryKeys.users.search(
      searchTerm.trim(),
      normalizeAnalyticsFilters(filters, defaultFilters),
      limit,
    ),
    queryFn: async ({ signal }) => {
      const normalizedFilters = normalizeAnalyticsFilters(filters, defaultFilters);
      const url = createAnalyticsUrl(BASE_URLS.USERS, '/search', normalizedFilters, {
        searchTerm,
        limit: limit.toString(),
      });
      return fetchAnalyticsData<UserSearchResult[]>(url, signal);
    },
    enabled: enabled && Boolean(searchTerm?.trim()),
    staleTime: CACHE_TIMES.USER_SEARCH_STALE_TIME,
    gcTime: CACHE_TIMES.GC_TIME,
    ...DEFAULT_QUERY_OPTIONS,
  });

  return useMemo(
    () => ({
      ...query,
      users: query.data || [],
    }),
    [query],
  );
};

export const useUserUtilization = ({
  firstName,
  lastName,
  filters,
  enabled = true,
}: UseUserUtilizationOptions) => {
  const defaultFilters = useMemo(() => createDefaultBaseFilters(), []);
  const normalizedFilters = useMemo(
    () => normalizeAnalyticsFilters(filters, defaultFilters),
    [filters, defaultFilters],
  );

  const query = useQuery<UserUtilizationDto>({
    queryKey: analyticsQueryKeys.users.utilization(
      firstName.trim(),
      lastName.trim(),
      normalizedFilters,
    ),
    queryFn: async ({ signal }) => {
      const url = createAnalyticsUrl(
        BASE_URLS.USERS,
        `/${encodeURIComponent(firstName.trim())}/${encodeURIComponent(lastName.trim())}`,
        normalizedFilters,
      );
      return fetchAnalyticsData<UserUtilizationDto>(url, signal);
    },
    enabled: enabled && Boolean(firstName?.trim() && lastName?.trim()),
    staleTime: CACHE_TIMES.USER_SEARCH_STALE_TIME,
    gcTime: CACHE_TIMES.GC_TIME,
    ...DEFAULT_QUERY_OPTIONS,
  });

  return useMemo(
    () => ({
      ...query,
      user: query.data,
    }),
    [query],
  );
};

export const useUserSearchWithDebounce = (
  searchTerm: string,
  filters?: BaseAnalyticsFilter,
  limit?: number,
  debounceMs = DEFAULTS.DEBOUNCE_MS,
) => {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, debounceMs]);

  return useUserSearch({
    searchTerm: debouncedSearchTerm,
    filters,
    limit,
    enabled: Boolean(debouncedSearchTerm?.trim()),
  });
};

export const useUserMonthlyExperiences = ({
  firstName,
  lastName,
  monthYear,
  filters,
  enabled = true,
}: UseUserMonthlyExperiencesOptions) => {
  const defaultFilters = useMemo(() => createDefaultBaseFilters(), []);
  const normalizedFilters = useMemo(
    () => normalizeAnalyticsFilters(filters, defaultFilters),
    [filters, defaultFilters],
  );

  const query = useQuery<UserMonthlyExperiencesDto>({
    queryKey: analyticsQueryKeys.users.monthlyExperiences(
      firstName.trim(),
      lastName.trim(),
      monthYear,
      normalizedFilters,
    ),
    queryFn: async ({ signal }) => {
      const url = createAnalyticsUrl(
        BASE_URLS.USERS,
        `/${encodeURIComponent(firstName.trim())}/${encodeURIComponent(lastName.trim())}/months/${encodeURIComponent(monthYear)}`,
        normalizedFilters,
      );
      return fetchAnalyticsData<UserMonthlyExperiencesDto>(url, signal);
    },
    enabled: enabled && Boolean(firstName?.trim() && lastName?.trim() && monthYear?.trim()),
    staleTime: CACHE_TIMES.USER_SEARCH_STALE_TIME,
    gcTime: CACHE_TIMES.GC_TIME,
    ...DEFAULT_QUERY_OPTIONS,
  });

  return useMemo(
    () => ({
      ...query,
      experiences: query.data?.experiences || [],
    }),
    [query],
  );
};
